/**
 * Unified preview capability check for documents.
 * Centralizes the logic for determining if a file can be previewed inline.
 */

import { isIfcFile } from "@/domains/documents/constants/fileTypes";

/** File extensions that can be previewed inline */
export const PREVIEWABLE_EXTENSIONS = new Set([
  // PDF
  ".pdf",
  // Images
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".bmp",
  ".svg",
  // Office documents (rendered via client-side JS libraries)
  ".docx",
  ".xlsx",
  ".xls",
  ".ods",
  // DWG files (previewed via converted child PDF)
  ".dwg",
  // IFC (BIM) files — previewed via the 3D viewer once the pipeline has
  // produced a glTF (state IFC_PROCESSED).
  ".ifc",
]);

/** MIME types that can be previewed inline */
const PREVIEWABLE_MIME_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel", // .xls
  "application/x-step", // .ifc (BIM)
  "application/vnd.oasis.opendocument.spreadsheet", // .ods
]);

/**
 * Check if a file can be previewed inline based on filename extension
 * and optional content type.
 */
export function canPreviewFile(filename: string, contentType?: string | null): boolean {
  // Check by extension first (most reliable)
  const lowerFilename = filename.toLowerCase();
  const ext = lowerFilename.slice(lowerFilename.lastIndexOf("."));
  if (PREVIEWABLE_EXTENSIONS.has(ext)) {
    return true;
  }

  // Fall back to content type check
  if (contentType && PREVIEWABLE_MIME_TYPES.has(contentType)) {
    return true;
  }

  // Also check generic image/* content type prefix
  if (contentType?.startsWith("image/")) {
    return true;
  }

  return false;
}

/**
 * States in which a .dwg parent has its preview child PDF available.
 *
 * Legacy flow: the child PDF exists from DWG_PROCESSED onward. DXF-first flow
 * (DWG_DXF_PIPELINE_ENABLED): the parent continues through the STANDARD
 * pipeline itself and the child PDF is filed at the end of DWG_EXTRACTING —
 * so every post-extract state also has it. Earlier states (DWG_CONTAINER,
 * DWG_CONVERTING, DWG_CONVERTED, DWG_EXTRACTING, ERROR) have no child yet.
 */
const DWG_PREVIEWABLE_STATES = new Set([
  "COMPLETE",
  "DWG_PROCESSED",
  "DWG_FINALIZING",
  "EXTRACTED",
  "CLASSIFIED",
  "TAGGED",
  "STAGED",
  "EMBEDDED",
]);

/**
 * Check if a document row can be previewed inline. Wraps canPreviewFile with
 * the DWG-state rule: a .dwg file is only previewable once its flow has
 * produced the child PDF (see DWG_PREVIEWABLE_STATES).
 */
export function canPreviewDocumentRow(doc: {
  filename: string;
  content_type: string | null;
  state: string | null;
}): boolean {
  if (doc.filename?.toLowerCase().endsWith(".dwg")) {
    return DWG_PREVIEWABLE_STATES.has(doc.state?.toUpperCase() ?? "");
  }
  // IFC: only previewable once the IfcProcessor has written a glTF sibling.
  if (isIfcFile(doc.filename)) {
    const s = doc.state?.toUpperCase();
    return s === "COMPLETE" || s === "IFC_PROCESSED";
  }
  return canPreviewFile(doc.filename, doc.content_type);
}

/** Check if a file is an Office document that needs client-side rendering */
export function isOfficeFile(filename: string): boolean {
  const lower = filename.toLowerCase();
  return (
    lower.endsWith(".docx") ||
    lower.endsWith(".xlsx") ||
    lower.endsWith(".xls") ||
    lower.endsWith(".ods")
  );
}

/** Check if a file is a Word document (.docx only — legacy .doc is not supported by docx-preview) */
export function isWordFile(filename: string): boolean {
  const lower = filename.toLowerCase();
  return lower.endsWith(".docx");
}

/**
 * Check if a file is a spreadsheet renderable by SheetJS.
 * Covers Excel (.xlsx, .xls) and OpenDocument (.ods) — SheetJS auto-detects
 * the format from bytes, so all three route to the same renderer.
 */
export function isExcelFile(filename: string): boolean {
  const lower = filename.toLowerCase();
  return lower.endsWith(".xlsx") || lower.endsWith(".xls") || lower.endsWith(".ods");
}

/**
 * Derive a best-effort MIME type from a file path's extension. Used when the
 * content_type isn't available from a document row (e.g., preview from a
 * version history where only `file_path` is passed through the callback).
 */
export function getContentTypeFromPath(filePath: string): string {
  const lower = filePath.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".bmp")) return "image/bmp";
  if (lower.endsWith(".svg")) return "image/svg+xml";
  if (lower.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".ods")) return "application/vnd.oasis.opendocument.spreadsheet";
  return "application/octet-stream";
}
